/* eslint-disable no-throw-literal */
/* eslint-disable array-callback-return */

import API from '../api/API2'
import Schema from '../schema'
import BluLicense from 'lib/license';

let handler = {
  get: (target, name) => {
    if (target[name] === undefined){
      if(target.fields && target.fields[name]){
        return target.fields[name]()
      }
      return target.params[name]
    }
    return target[name]
  },
  ownKeys: function (target, key) {
    return [...Object.keys(target.params), ...Object.keys(target.fields)]
  },
  getOwnPropertyDescriptor: function (target, name) {
    var value;
    if (!target[name]){
      if(target.fields && target.fields[name]){
        value = target.fields[name]()
      } else {
        value = target.params[name]
      }
    } else {
      value = target[name]
    }
    return value !== undefined ? {
      value: value,
      writable: true,
      enumerable: true,
      configurable: true
    } : undefined;
  },
}

export class BaseModel {

  updateOnSave = true

  constructor(params, schema) {
    this.schema = schema
    this._sideEffects = {}
    this.setDirty(false)
    this.error = false
    this.params = {}
    this.fields = {}

    this.set(params || {}, false)
    // reset _original_params after set, because this is init
    this._original_params = {}
    return new Proxy(this, handler)
  }

  useEffect(func, keys) {
    keys.map(attr=> {
      if (this._sideEffects[attr] === undefined){
        this._sideEffects[attr] = []
      }
      this._sideEffects[attr] = func
    })
  }

  setDirty(dirty) {
    if(this.dirty !== dirty && dirty === false){
      this._original_params = {}
    }
    this.dirty = dirty
    this._validAPIParms = {}
  }

  set(params, validate) {
    if (typeof params !== 'object') {
      // todo: make this error better
      throw "Invalid params"
    }
    this.hash = params.__hash
    delete params.__hash

    const schema = Schema.getSchema(this.schema)
    const sideEffects = []

    // Loop over params being set, and backup original values
    Object.keys(params).map(key => {
      if(this._sideEffects[key] && !sideEffects.includes(this._sideEffects[key])){
        sideEffects.push(this._sideEffects[key])
      }
      if(params[key] !== undefined && params[key] !== null){
        if (this._original_params[key] === undefined){
          this._original_params[key] = this.params[key]
        }

        // Warning: only mixes first level, will clobber deep objects
        if (schema.schema.properties[key]) {
          switch(schema.schema.properties[key].type){ /* eslint-disable-line */
            case 'number':
              params[key] = parseInt(params[key])
          }
        }
      } else {
        delete params[key]
        delete this.params[key]
      }
    })

    this.params = {...this.params, ...params}

    // todo: this should probably check that this.params actually changed
    this.setDirty(true)

    const valid =  validate ? this.validate() : true
    sideEffects.map(func => func())

    return valid
  }

  unset(key, validate) {
    if (typeof key !== 'string') {
      // todo: make this error better
      throw "Invalid key"
    }
    delete this.params[key]
    return validate ? this.validate() : true
  }

  reset(key) {
    if(key === undefined) {
      Object.keys(this._original_params).map(k => {
        this.params[k] = this._original_params[k];
      })
      this.setDirty(false)
      this.error = false
    } else if(this._original_params[key] !== undefined) {
      this.params = this._original_params[key];
      delete this._original_params[key]
      if (Object.keys(this._original_params).length === 0) {
        this.setDirty(false)
      }
    }
  }

  getDirtyFields() {
    return Object.keys(this._original_params)
  }

  getAPIValues(){
    return Object.keys(this._validAPIParms).length > 0 ? this._validAPIParms : this.params
  }

  validate() {
    const modelValidate = Schema.getSchema(this.schema)
    const licenseValidate = BluLicense.getSchema(this.schema)
    const _recurseValidateCheck = (data, schema, original_params, validAPIParms) => {
      Object.keys(schema.properties).map(k => {
        // todo: need to recursivly prune any empty branch
        if(validAPIParms){
          validAPIParms[k] = data[k]
        }
        if (schema.properties[k].properties) {
          _recurseValidateCheck(data[k], schema.properties[k], original_params[k] || {}, validAPIParms[k])
        } else if ((schema.properties[k].readOnly && data[k] !== undefined) || (schema.properties[k].createOnly && !!data.id)) {
          if(original_params[k] !== undefined){
            // reset readonly params
            data[k] = original_params[k]
            validAPIParms[k] = data[k]
          }
        } else if ((!schema.properties[k].readOnly && original_params[k]) || (schema.required && schema.required.includes(k)) ) {
          // if the data is not read only && data is dirty put it in
          // if the data is required put it in
          validAPIParms[k] = data[k]
        }
      })
    }

    // check both the model schema, as well as the license schema, if applicable
    [modelValidate, licenseValidate].forEach(validate=>{
      if(validate){
        _recurseValidateCheck(this.params, validate.schema, this._original_params, this._validAPIParms)
      }
    })
    if (modelValidate(this._validAPIParms) === false) {
      return modelValidate.errors
    } else if (licenseValidate && licenseValidate(this._validAPIParms) === false){
      return licenseValidate.errors
    } else {
      return true
    }
  }

  validationMessages() {
    const regex = /\.([^[]+)(\[[\d]\])*/
    const _valid = {}

    const validate = Schema.getSchema(this.schema)
    if (validate(this.params) !== true) {
      validate.errors.map(message => {
        switch(message.keyword) {
          case 'required':
            _valid[message.params.missingProperty] = 'Required'
            break
          case 'additionalProperties': /* eslint-disable-line */
            _valid[''] = message.message
            break
          default: /* eslint-disable-line */
            const re = regex.exec(message.dataPath)
            if (re) {
              if (re[2]){
                // we can use this to flag which attribute is the problem
              }
              _valid[re[1]] = message.message
            } else {
              console.warn('Unhandled validation message', message)
            }
        }
      })
    }
    return _valid
  }

  getSchema() {
    return Schema.getSchema(this.schema)
  }

  create() {
    if (this.validate() !== true) {
      throw "TODO: This should raise a json validation error"
    }
    const api = new API()
    return api.post(this.schema, this.params)
      .then((response) => {
        if(this.updateOnSave){
          this.set(response.data)
          this.setDirty(false);
          this.error = false;
        }
        return this
      }).catch(error => {
        this.error = error;
        throw error.message;
      })
  }

  execute() {
    if (this.validate() !== true) {
      throw "TODO: This should raise a json validation error"
    }
    const api = new API()
    return api.rpc(this.schema, this.params)
      .then((response) => {
        this.setDirty(false);
        this.error = false;
        return response.data
      }).catch(error => {
        this.error = error;
        throw error.message;
      })
  }

  read() {
    const api = new API()
    return api.get(this.schema, this.params)
      .then((response) => {
        this.set(response.data)
        this.setDirty(false);
        this.error = false;
        return this
      })
      .catch(error => {
        this.error = error;
        throw error;
      })
  }

  update() {
    const isValid = this.validate()
    if (isValid !== true) {
      throw "TODO: This should raise a json validation error"
    }

    const api = new API()
    return api.put(this.schema, this.getAPIValues(), this.params.id)
      .then(response => {
        if(this.updateOnSave){
          this.set(response.data)
          this.setDirty(false);
          this.error = false;
        }
        return this
      }).catch(error => {
        this.error = error;
        throw error.message;
      })
  }

  delete() {
    const api = new API()
    // todo: if not this.id, throw error
    return api.delete(this.schema, this.id)
  }
}
